import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@ui-kit';
import { suggestRoute } from '../lib/suggestRoute';
import { useCommandPaletteContext } from '../context/CommandPaletteContext';

/**
 * NotFoundPage — 미매칭 라우트(404) fallback.
 *
 * §UAT-2026-07-08-R650 (queue #650) §R650-3: `router.tsx` 의 `<Routes>` 말단
 * `path="*"` catch-all 이 렌더하는 페이지. ui-kit `EmptyState` 재사용(재구현 금지).
 * `AuthLayout` 로 감싸져 렌더되므로(AppShell 유지) 이 컴포넌트 자체는 본문 영역만
 * 담당한다.
 *
 * queue #680 §R680-4 — 유사 라우트 제안(사전 매핑 + 레벤슈타인 fallback, `suggestRoute.ts`)과
 * "통합검색 열기(Ctrl+K)" 버튼을 추가한다. 팔레트는 `AuthLayout` 소유 단일 인스턴스를
 * `CommandPaletteContext` 로 재사용(신규 팔레트 0). Provider 부재(단독 렌더) 시 버튼은
 * 전역 Ctrl+K keydown 리스너(`useCommandPalette`)에게 이벤트를 디스패치하는 fallback 을 쓴다.
 */
export function NotFoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const commandPalette = useCommandPaletteContext();

  const suggestions = suggestRoute(location.pathname);

  const openSearch = () => {
    if (commandPalette) {
      commandPalette.open();
      return;
    }
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
  };

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      <EmptyState
        title={t('common.notFound.title')}
        description={t('common.notFound.description', { path: location.pathname })}
        action={
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-4)',
              alignItems: 'center',
            }}
          >
            {suggestions.length > 0 && (
              <div data-testid="notfound-suggestions" style={{ textAlign: 'center' }}>
                <p
                  style={{
                    margin: '0 0 var(--spacing-2) 0',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {t('common.notFound.suggestTitle')}
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-1)',
                  }}
                >
                  {suggestions.map((s, i) => (
                    <li key={s.path}>
                      <Link
                        to={s.path}
                        data-testid={`notfound-suggestion-${i}`}
                        aria-label={`${t('common.notFound.suggestAria')}: ${s.path}`}
                        style={{ color: 'var(--color-info)', fontFamily: 'var(--font-family-mono)' }}
                      >
                        {s.path}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/_index')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('common.notFound.goHome')}
              </button>
              <button
                type="button"
                data-testid="notfound-open-search-btn"
                onClick={openSearch}
                aria-label={t('common.notFound.openSearchAria')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('common.notFound.openSearch')}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
}
